import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { PLANBASE_CONFIG } from '../config';
import { decodeJWT } from '../utils/auth';
import { checkAdminAccess } from '../lib/admin';

import {
  ArrowUturnLeftIcon,
  BookmarkIcon,
  CheckCircleIcon,
  ChartBarIcon,
  ClockIcon,
  EyeIcon,
  HeartIcon,
  MagnifyingGlassIcon,
  PencilSquareIcon,
  ShieldCheckIcon,
  Squares2X2Icon,
  TrashIcon,
  UsersIcon,
  XCircleIcon,
} from '@heroicons/react/24/outline';

interface ProjectSubmission {
  id: string;
  database_id: string;
  data: {
    title: string;
    description: string;
    category?: string;
    style?: string;
    location?: string;
    year?: string;
    architect?: string;
    coverImage?: string;
    images?: any[];
    tags?: string[];
    status?: string;
    created_by?: string;
  };
  status: string;
  slug?: string;
  created_at: string;
  updated_at: string;
  created_by: string;
}

interface AdminUser {
  id: string;
  email: string;
  name?: string;
  role?: string;
  status?: string;
  tier?: string;
  created_at: string;
}

const STATUS_CONFIG = {
  published: { label: 'Published', icon: CheckCircleIcon, className: 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400' },
  pending:   { label: 'Pending',   icon: ClockIcon,        className: 'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-700 dark:text-yellow-400' },
  archived:  { label: 'Archived',  icon: XCircleIcon,      className: 'bg-muted text-muted-foreground' },
  draft:     { label: 'Draft',     icon: PencilSquareIcon,         className: 'bg-muted text-muted-foreground' },
} as const;

const FILTERS = ['pending', 'published', 'draft', 'archived', 'all'] as const;

interface AdminStats {
  totals: {
    projects: number;
    pending: number;
    published: number;
    draft: number;
    users: number;
    views: number;
    likes: number;
    bookmarks: number;
  };
  last7d: { newUsers: number; newProjects: number };
  perProject: Array<{
    id: string;
    title: string;
    status: string;
    slug?: string;
    created_at: string;
    views: number;
    likes: number;
    bookmarks: number;
  }>;
}

export default function Admin() {
  const navigate = useNavigate();
  const [projects, setProjects] = useState<ProjectSubmission[]>([]);
  const [users, setUsers] = useState<AdminUser[]>([]);
  const [adminStats, setAdminStats] = useState<AdminStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  // Default to pending so the review backlog is visible the moment the
  // admin opens the page — that's the first job to do, not "browse all."
  const [filter, setFilter] = useState<string>('pending');
  const [searchQuery, setSearchQuery] = useState('');
  const [activeTab, setActiveTab] = useState<'projects' | 'stats' | 'users'>('projects');
  const [statsSort, setStatsSort] = useState<'views' | 'likes' | 'bookmarks'>('views');
  const [isAdmin, setIsAdmin] = useState(false);

  useEffect(() => { checkAdminAccessStatus(); }, []);

  const checkAdminAccessStatus = async () => {
    try {
      const token = localStorage.getItem('planbase_token');
      const userData = localStorage.getItem('planbase_user');
      if (!token) { setError('Please log in to access admin'); setLoading(false); return; }
      const payload = decodeJWT(token);
      const userId = payload?.sub || payload?.user_id || payload?.id;
      let email = payload.email;
      if (!email && userData) { try { email = JSON.parse(userData).email; } catch {} }
      const hasAccess = await checkAdminAccess(userId, email);
      if (hasAccess) { setIsAdmin(true); Promise.all([fetchProjects(), fetchUsers(), fetchStats()]); }
      else { setError('You do not have permission to access this page'); setLoading(false); }
    } catch { setError('Failed to verify admin access'); setLoading(false); }
  };

  const fetchProjects = async () => {
    try {
      const token = localStorage.getItem('planbase_token');
      if (!token) { setError('Please log in to access admin'); setLoading(false); return; }
      const response = await fetch(`${PLANBASE_CONFIG.apiUrl}/sdk/planbase/admin/portfolios`, {
        headers: {
          'Authorization': `Bearer ${PLANBASE_CONFIG.apiKey}`,
          'X-Project-ID': PLANBASE_CONFIG.projectId,
          'X-User-Token': token,
        },
      });
      const text = await response.text();
      if (response.ok) {
        const data = JSON.parse(text);
        const arr = Array.isArray(data.data?.data) ? data.data.data :
                    Array.isArray(data.data) ? data.data :
                    Array.isArray(data.portfolios) ? data.portfolios :
                    Array.isArray(data) ? data : [];
        setProjects(arr);
      } else {
        let err: any = {};
        try { err = JSON.parse(text); } catch { err = { error: text }; }
        setError(err.error || err.message || `Failed to load projects (${response.status})`);
      }
    } catch { setError('Failed to load projects'); }
    finally { setLoading(false); }
  };

  const fetchStats = async () => {
    try {
      const token = localStorage.getItem('planbase_token');
      if (!token) return;
      const response = await fetch(`${PLANBASE_CONFIG.apiUrl}/sdk/planbase/admin/stats`, {
        headers: {
          'Authorization': `Bearer ${PLANBASE_CONFIG.apiKey}`,
          'X-Project-ID': PLANBASE_CONFIG.projectId,
          'X-User-Token': token,
        },
      });
      if (!response.ok) return;
      const data = await response.json();
      setAdminStats(data);
    } catch { /* non-blocking */ }
  };

  const fetchUsers = async () => {
    try {
      const token = localStorage.getItem('planbase_token');
      if (!token) return;
      const response = await fetch(`${PLANBASE_CONFIG.apiUrl}/sdk/planbase/admin/members`, {
        headers: {
          'Authorization': `Bearer ${PLANBASE_CONFIG.apiKey}`,
          'X-Project-ID': PLANBASE_CONFIG.projectId,
          'X-User-Token': token,
        },
      });
      if (!response.ok) return;
      const data = await response.json();
      setUsers(Array.isArray(data.data) ? data.data : Array.isArray(data.members) ? data.members : []);
    } catch {}
  };

  const updateUser = async (userId: string, field: 'role' | 'status', value: string) => {
    try {
      const token = localStorage.getItem('planbase_token');
      if (!token) return;
      const response = await fetch(`${PLANBASE_CONFIG.apiUrl}/sdk/planbase/admin/members/${userId}`, {
        method: 'PATCH',
        headers: {
          'Authorization': `Bearer ${PLANBASE_CONFIG.apiKey}`,
          'X-Project-ID': PLANBASE_CONFIG.projectId,
          'X-User-Token': token,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ [field]: value }),
      });
      if (response.ok) fetchUsers();
    } catch {}
  };

  const updateProjectStatus = async (projectId: string, newStatus: string) => {
    try {
      const token = localStorage.getItem('planbase_token');
      if (!token) return;
      const res = await fetch(`${PLANBASE_CONFIG.apiUrl}/sdk/planbase/admin/portfolios/${projectId}`, {
        method: 'PATCH',
        headers: {
          'Authorization': `Bearer ${PLANBASE_CONFIG.apiKey}`,
          'X-Project-ID': PLANBASE_CONFIG.projectId,
          'X-User-Token': token,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ status: newStatus }),
      });
      if (res.ok) {
        // Send approval notification only when transitioning TO published.
        // Unapprove (back to draft) is a silent operation — don't ping the
        // uploader every time we triage.
        if (newStatus === 'published') {
          const project = projects.find(p => p.id === projectId);
          if (project?.created_by) {
            await sendApprovalNotification(projectId, project.created_by, project.data.title);
          }
        }
        fetchProjects();
        fetchStats();
      }
    } catch {}
  };

  const sendApprovalNotification = async (projectId: string, userId: string, projectTitle?: string) => {
    try {
      await fetch(`${PLANBASE_CONFIG.apiUrl}/sdk/notifications/send`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${PLANBASE_CONFIG.apiKey}`,
          'X-Project-ID': PLANBASE_CONFIG.projectId,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          user_id: userId,
          type: 'project_approved',
          title: 'Your project was approved! 🎉',
          body: `"${projectTitle || 'Your project'}" is now live on Planbase.`,
          data: { project_id: projectId },
        }),
      });
    } catch {
      // Notification failure shouldn't block the publish action
    }
  };

  const deleteProject = async (projectId: string) => {
    if (!confirm('Are you sure you want to delete this project?')) return;
    try {
      const token = localStorage.getItem('planbase_token');
      if (!token) return;
      const res = await fetch(`${PLANBASE_CONFIG.apiUrl}/sdk/planbase/admin/portfolios/${projectId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${PLANBASE_CONFIG.apiKey}`,
          'X-Project-ID': PLANBASE_CONFIG.projectId,
          'X-User-Token': token,
        },
      });
      if (res.ok) fetchProjects();
    } catch {}
  };

  const getProjectStatus = (p: ProjectSubmission) => p.status || p.data?.status || 'draft';

  const filteredProjects = projects.filter(p => {
    const s = getProjectStatus(p);
    const matchesFilter = filter === 'all' || s === filter;
    const matchesSearch =
      p.data?.title?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      p.data?.architect?.toLowerCase().includes(searchQuery.toLowerCase());
    return matchesFilter && matchesSearch;
  });

  const stats = {
    total:     projects.length,
    published: projects.filter(p => getProjectStatus(p) === 'published').length,
    pending:   projects.filter(p => getProjectStatus(p) === 'pending').length,
    draft:     projects.filter(p => getProjectStatus(p) === 'draft').length,
  };

  if (loading) {
    return (
      <div className="max-w-5xl mx-auto space-y-4">
        <div className="h-5 w-24 bg-muted rounded animate-pulse" />
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {[1,2,3,4].map(i => <div key={i} className="h-20 bg-muted rounded-2xl animate-pulse" />)}
        </div>
        <div className="h-96 bg-muted rounded-2xl animate-pulse" />
      </div>
    );
  }

  if (!isAdmin && error) {
    return (
      <div className="max-w-md mx-auto mt-16">
        <div className="bg-card border border-border rounded-2xl p-10 text-center">
          <div className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-red-100 dark:bg-red-900/30 mb-4">
            <ShieldCheckIcon className="h-6 w-6 text-red-600 dark:text-red-400" />
          </div>
          <p className="text-sm font-semibold text-foreground uppercase tracking-wide mb-1">Access Denied</p>
          <p className="text-sm text-muted-foreground mb-6">{error}</p>
          <button
            onClick={() => navigate('/discover')}
            className="px-5 py-2 bg-primary text-primary-foreground rounded-lg text-sm font-medium hover:bg-primary/90 transition-colors"
          >
            Go to Discover
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-5xl mx-auto space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h1 className="text-sm font-semibold text-foreground uppercase tracking-wide">Admin</h1>
        <span className="text-xs text-muted-foreground">{stats.total} total projects</span>
      </div>

      <div className="flex gap-1 border-b border-border">
        {(['projects', 'stats', 'users'] as const).map(tab => {
          const badge =
            tab === 'projects' ? stats.pending :
            tab === 'users' ? users.length :
            null;
          return (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              className={`px-3 py-2 text-xs font-medium capitalize border-b-2 transition-colors flex items-center gap-1.5 ${activeTab === tab ? 'border-primary text-foreground' : 'border-transparent text-muted-foreground hover:text-foreground'}`}
            >
              {tab}
              {badge !== null && badge > 0 && (
                <span className={`inline-flex items-center justify-center min-w-[1.25rem] h-4 px-1 rounded-full text-[10px] font-medium ${tab === 'projects' && stats.pending > 0 ? 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/40 dark:text-yellow-400' : 'bg-muted text-muted-foreground'}`}>
                  {badge}
                </span>
              )}
            </button>
          );
        })}
      </div>

      {error && (
        <div className="bg-red-50 dark:bg-red-950/30 border border-red-200 dark:border-red-800 rounded-2xl px-4 py-3">
          <p className="text-sm text-red-600 dark:text-red-400">{error}</p>
        </div>
      )}

      {activeTab === 'projects' && (
        <>
        {/* Project status overview — shown only on the Projects tab so it
            doesn't duplicate the Stats dashboard's own KPIs. */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {[
            { label: 'Total', value: stats.total, color: 'text-foreground' },
            { label: 'Published', value: stats.published, color: 'text-green-600 dark:text-green-400' },
            { label: 'Pending', value: stats.pending, color: 'text-yellow-600 dark:text-yellow-400' },
            { label: 'Drafts', value: stats.draft, color: 'text-muted-foreground' },
          ].map(s => (
            <div key={s.label} className="bg-card border border-border rounded-2xl p-4">
              <p className="text-xs text-muted-foreground mb-1">{s.label}</p>
              <p className={`text-2xl font-medium font-mono tabular-nums ${s.color}`}>{s.value}</p>
            </div>
          ))}
        </div>

        {/* Filters + Search */}
        <div className="flex flex-col sm:flex-row gap-3">
        <div className="flex gap-1.5 flex-wrap">
          {FILTERS.map(f => {
            const n =
              f === 'all' ? stats.total :
              f === 'pending' ? stats.pending :
              f === 'published' ? stats.published :
              f === 'draft' ? stats.draft :
              projects.filter(p => getProjectStatus(p) === 'archived').length;
            return (
              <button
                key={f}
                onClick={() => setFilter(f)}
                className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors capitalize inline-flex items-center gap-1.5 ${
                  filter === f
                    ? 'bg-primary text-primary-foreground'
                    : 'bg-card border border-border text-muted-foreground hover:text-foreground hover:bg-muted'
                }`}
              >
                <span>{f}</span>
                <span className={`inline-flex items-center justify-center min-w-[1.25rem] h-4 px-1 rounded text-[10px] font-mono ${filter === f ? 'bg-primary-foreground/20 text-primary-foreground' : 'bg-muted-foreground/10 text-muted-foreground'}`}>
                  {n}
                </span>
              </button>
            );
          })}
        </div>
        <div className="flex-1 relative">
          <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
          <input
            type="text"
            placeholder="Search projects..."
            value={searchQuery}
            onChange={e => setSearchQuery(e.target.value)}
            className="w-full pl-9 pr-4 py-1.5 border border-border rounded-lg text-sm bg-card text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-ring"
          />
        </div>
        </div>

        {/* Projects List */}
        <div className="bg-card border border-border rounded-2xl overflow-hidden">
        {filteredProjects.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-20 text-center">
            <Squares2X2Icon className="h-8 w-8 text-muted-foreground/40 mb-3" />
            <p className="text-sm text-muted-foreground">No projects found</p>
          </div>
        ) : (
          filteredProjects.map((project, i) => {
            const status = getProjectStatus(project) as keyof typeof STATUS_CONFIG;
            const cfg = STATUS_CONFIG[status] ?? STATUS_CONFIG.draft;
            const StatusIcon = cfg.icon;
            return (
              <div key={project.id}>
                {i > 0 && <div className="border-t border-border mx-4" />}
                <div className="flex items-center gap-4 px-4 py-3.5 hover:bg-muted/40 transition-colors">
                  {/* Thumbnail */}
                  <img
                    src={project.data.coverImage}
                    alt={project.data.title}
                    className="w-11 h-11 rounded-xl object-cover flex-shrink-0 bg-muted"
                    onError={e => { e.currentTarget.src = 'https://images.unsplash.com/photo-1600596542815-ffad4c1539a9?w=100&h=100&fit=crop'; }}
                  />

                  {/* Info */}
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-foreground truncate">{project.data.title || 'Untitled'}</p>
                    <p className="text-xs text-muted-foreground truncate mt-0.5">
                      {[project.data.architect, project.data.category].filter(Boolean).join(' · ') || '—'}
                    </p>
                    {/* Uploader ID for admin outreach */}
                    {project.created_by && (
                      <p className="text-[10px] text-muted-foreground/60 truncate mt-0.5 font-mono">
                        by {project.created_by}
                      </p>
                    )}
                  </div>

                  {/* Status badge */}
                  <span className={`hidden sm:inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium flex-shrink-0 ${cfg.className}`}>
                    <StatusIcon className="h-3 w-3" />
                    {cfg.label}
                  </span>

                  {/* Date */}
                  <span className="hidden md:block text-xs text-muted-foreground flex-shrink-0 w-20 text-right">
                    {new Date(project.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: '2-digit' })}
                  </span>

                  {/* Actions */}
                  <div className="flex items-center gap-1 flex-shrink-0">
                    <button
                      onClick={() => navigate(`/project/${project.slug || project.id}`)}
                      className="p-1.5 rounded-lg text-muted-foreground hover:text-primary hover:bg-orange-50 dark:hover:bg-orange-950/40 transition-colors"
                      title="View"
                    >
                      <EyeIcon className="h-3.5 w-3.5" />
                    </button>
                    <button
                      onClick={() => navigate(`/edit-project/${project.id}`)}
                      className="p-1.5 rounded-lg text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
                      title="Edit"
                    >
                      <PencilSquareIcon className="h-3.5 w-3.5" />
                    </button>
                    {/* Approve — only shows for pending/draft; sends uploader notification */}
                    {(status === 'pending' || status === 'draft') && (
                      <button
                        onClick={() => updateProjectStatus(project.id, 'published')}
                        className="inline-flex items-center gap-1 px-2.5 py-1 rounded-lg text-xs font-medium bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400 hover:bg-green-200 dark:hover:bg-green-900/60 transition-colors"
                        title="Approve and publish — notifies the uploader"
                      >
                        <CheckCircleIcon className="h-3.5 w-3.5" />
                        Approve
                      </button>
                    )}
                    {/* Unapprove — only for published; silent revert to draft */}
                    {status === 'published' && (
                      <button
                        onClick={() => {
                          if (confirm(`Unpublish "${project.data.title || 'this project'}"? It will revert to Draft and will no longer appear on Discover.`)) {
                            updateProjectStatus(project.id, 'draft');
                          }
                        }}
                        className="inline-flex items-center gap-1 px-2.5 py-1 rounded-lg text-xs font-medium bg-yellow-100 dark:bg-yellow-900/30 text-yellow-700 dark:text-yellow-400 hover:bg-yellow-200 dark:hover:bg-yellow-900/60 transition-colors"
                        title="Unapprove — reverts to Draft and removes from Discover"
                      >
                        <ArrowUturnLeftIcon className="h-3.5 w-3.5" />
                        Unapprove
                      </button>
                    )}
                    <button
                      onClick={() => deleteProject(project.id)}
                      className="p-1.5 rounded-lg text-muted-foreground hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-950/40 transition-colors"
                      title="Delete"
                    >
                      <TrashIcon className="h-3.5 w-3.5" />
                    </button>
                  </div>
                </div>
              </div>
            );
          })
        )}
        </div>
        </>
      )}

      {activeTab === 'stats' && (
        <div className="space-y-4">
          {/* Headline KPIs — the four engagement signals + their 7-day context */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {[
              { label: 'Total views',     value: adminStats?.totals.views ?? 0,     icon: EyeIcon,      color: 'text-foreground' },
              { label: 'Total likes',     value: adminStats?.totals.likes ?? 0,     icon: HeartIcon,    color: 'text-red-600 dark:text-red-400' },
              { label: 'Total bookmarks', value: adminStats?.totals.bookmarks ?? 0, icon: BookmarkIcon, color: 'text-primary' },
              { label: 'Registered users',value: adminStats?.totals.users ?? 0,     icon: UsersIcon,    color: 'text-foreground' },
            ].map((s) => {
              const SIcon = s.icon;
              return (
                <div key={s.label} className="bg-card border border-border rounded-2xl p-4">
                  <div className="flex items-center justify-between mb-2">
                    <p className="text-xs text-muted-foreground">{s.label}</p>
                    <SIcon className={`h-3.5 w-3.5 ${s.color} opacity-70`} />
                  </div>
                  <p className={`text-2xl font-medium font-mono tabular-nums ${s.color}`}>
                    {s.value.toLocaleString()}
                  </p>
                </div>
              );
            })}
          </div>

          {/* 7-day deltas */}
          <div className="grid grid-cols-2 gap-3">
            <div className="bg-card border border-border rounded-2xl p-4">
              <p className="text-xs text-muted-foreground mb-1">New users · last 7 days</p>
              <p className="text-xl font-medium font-mono tabular-nums text-foreground">
                +{(adminStats?.last7d.newUsers ?? 0).toLocaleString()}
              </p>
            </div>
            <div className="bg-card border border-border rounded-2xl p-4">
              <p className="text-xs text-muted-foreground mb-1">New projects · last 7 days</p>
              <p className="text-xl font-medium font-mono tabular-nums text-foreground">
                +{(adminStats?.last7d.newProjects ?? 0).toLocaleString()}
              </p>
            </div>
          </div>

          {/* Per-project interaction table — sortable, top 20 */}
          <div className="bg-card border border-border rounded-2xl overflow-hidden">
            <div className="flex items-center justify-between px-4 py-3 border-b border-border">
              <div>
                <p className="text-sm font-medium text-foreground">Top projects by {statsSort}</p>
                <p className="text-xs text-muted-foreground mt-0.5">Showing top 20 of {adminStats?.perProject.length ?? 0}</p>
              </div>
              <div className="flex items-center gap-1 text-xs">
                {(['views', 'likes', 'bookmarks'] as const).map(k => (
                  <button
                    key={k}
                    onClick={() => setStatsSort(k)}
                    className={`px-2 py-1 rounded-md transition-colors capitalize ${statsSort === k ? 'bg-muted text-foreground' : 'text-muted-foreground hover:text-foreground'}`}
                  >
                    {k}
                  </button>
                ))}
              </div>
            </div>
            {!adminStats || adminStats.perProject.length === 0 ? (
              <div className="py-16 text-center">
                <ChartBarIcon className="h-8 w-8 text-muted-foreground/40 mb-3 mx-auto" />
                <p className="text-sm text-muted-foreground">No interactions recorded yet</p>
              </div>
            ) : (
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-muted/40 text-xs text-muted-foreground uppercase tracking-wide">
                    <th className="text-left px-4 py-2 font-medium">Project</th>
                    <th className="text-left px-3 py-2 font-medium w-24">Status</th>
                    <th className="text-right px-3 py-2 font-medium w-20">Views</th>
                    <th className="text-right px-3 py-2 font-medium w-20">Likes</th>
                    <th className="text-right px-4 py-2 font-medium w-24">Bookmarks</th>
                  </tr>
                </thead>
                <tbody>
                  {[...adminStats.perProject]
                    .sort((a, b) => b[statsSort] - a[statsSort])
                    .slice(0, 20)
                    .map((p, i) => {
                      const sCfg = STATUS_CONFIG[p.status as keyof typeof STATUS_CONFIG] ?? STATUS_CONFIG.draft;
                      return (
                        <tr key={p.id} className={`${i > 0 ? 'border-t border-border' : ''} hover:bg-muted/30 transition-colors`}>
                          <td className="px-4 py-2.5">
                            <button
                              onClick={() => navigate(`/project/${p.slug || p.id}`)}
                              className="text-left text-foreground hover:text-primary truncate max-w-[18rem] block"
                              title={p.title}
                            >
                              {p.title}
                            </button>
                          </td>
                          <td className="px-3 py-2.5">
                            <span className={`inline-flex items-center text-[10px] px-1.5 py-0.5 rounded font-medium ${sCfg.className}`}>
                              {sCfg.label}
                            </span>
                          </td>
                          <td className="px-3 py-2.5 text-right font-mono tabular-nums text-foreground">{p.views.toLocaleString()}</td>
                          <td className="px-3 py-2.5 text-right font-mono tabular-nums text-foreground">{p.likes.toLocaleString()}</td>
                          <td className="px-4 py-2.5 text-right font-mono tabular-nums text-foreground">{p.bookmarks.toLocaleString()}</td>
                        </tr>
                      );
                    })}
                </tbody>
              </table>
            )}
          </div>
        </div>
      )}

      {activeTab === 'users' && <div className="bg-card border border-border rounded-2xl overflow-hidden">
        <div className="px-4 py-3 border-b border-border">
          <p className="text-sm font-medium text-foreground">All users</p>
          <p className="text-xs text-muted-foreground mt-0.5">{users.length} registered users</p>
        </div>
        {users.length === 0 ? (
          <p className="px-4 py-10 text-center text-sm text-muted-foreground">No users found</p>
        ) : (
          users.map((user, i) => (
            <div key={user.id} className={`flex items-center gap-3 px-4 py-3 ${i > 0 ? 'border-t border-border' : ''}`}>
              <div className="flex-1 min-w-0">
                <p className="text-sm text-foreground truncate">{user.name || 'Unnamed user'}</p>
                <p className="text-xs text-muted-foreground truncate">{user.email}</p>
              </div>
              <select value={user.role || 'member'} onChange={e => updateUser(user.id, 'role', e.target.value)} className="text-xs bg-background border border-border rounded px-2 py-1 text-foreground">
                <option value="member">Member</option>
                <option value="admin">Admin</option>
              </select>
              <select value={user.status || 'active'} onChange={e => updateUser(user.id, 'status', e.target.value)} className="text-xs bg-background border border-border rounded px-2 py-1 text-foreground">
                <option value="active">Active</option>
                <option value="suspended">Suspended</option>
              </select>
            </div>
          ))
        )}
      </div>}
    </div>
  );
}
